import { Box, Button, Group, Modal, Stack, Tabs, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useState } from 'react';

import { TransitHouseConsignment } from '@e-origin/shared';

import { classes } from '@styles';
import { TransitViewHouseFormValues } from '../../validateTransitViewHouseForm';
import { HouseBatches } from './HouseBatches';
import { HouseConsignmentData } from './HouseConsignmentData';
import { HouseDocuments } from './HouseDocuments';
import { HouseStakeholders } from './HouseStakeholders';
import { HouseTransport } from './HouseTransport';
import { useTransitViewEditHouseFormInitializer } from './useTransitViewEditHouseFormInitializer';

interface EditHouseProps {
  form: UseFormReturnType<TransitViewHouseFormValues>;
  houseConsignmentIdx?: number;
  editDisabled: boolean;
  editDisabledConditionalForArrival: boolean;
  onHide: () => void;
  onSave: (houseConsignment: TransitHouseConsignment) => void;
  isArrival: boolean;
}

export const EditHouse: React.FC<EditHouseProps> = ({
  form,
  houseConsignmentIdx,
  onHide,
  onSave,
  editDisabled,
  editDisabledConditionalForArrival,
  isArrival,
}) => {
  const [showOverwriteConfirmationModal, setShowOverwriteConfirmationModal] = useState(false);
  const { houseForm, onSubmit } = useTransitViewEditHouseFormInitializer({ houseConsignmentIdx, form, onSave, onHide });

  const isDataOverwritten = (house: Partial<TransitHouseConsignment>) => {
    const oldHouse = form?.values?.Consignment.HouseConsignment[houseConsignmentIdx ?? -1];
    if (
      oldHouse?.ConsignmentItem?.some(
        (item) =>
          (house.countryOfDispatch && item.countryOfDispatch) ||
          (house.countryOfDestination && item.countryOfDestination) ||
          (house.referenceNumberUCR && item.referenceNumberUCR),
      )
    ) {
      return true;
    }

    return false;
  };

  const handleSave = () => {
    if (isDataOverwritten(houseForm.values)) {
      setShowOverwriteConfirmationModal(true);
      return;
    }
    onSubmit();
  };

  return (
    <Stack px="md">
      <Modal
        opened={showOverwriteConfirmationModal}
        onClose={() => setShowOverwriteConfirmationModal(false)}
        title="Confirm save"
        centered
        withCloseButton={false}
        zIndex={1000}
      >
        <Text>There are some headers values that had been defined. Saving will overwrite the lower level values. Are you sure you want to continue?</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setShowOverwriteConfirmationModal(false)}>
            Cancel
          </Button>
          <Button variant="filled" onClick={onSubmit}>
            Continue
          </Button>
        </Group>
      </Modal>

      <Tabs defaultValue="house-consignment">
        <Tabs.List>
          <Tabs.Tab value="house-consignment">House Consignment</Tabs.Tab>
          <Tabs.Tab value="stakeholders">Stakeholders</Tabs.Tab>
          <Tabs.Tab value="transport">Transport</Tabs.Tab>
          <Tabs.Tab value="documents">Documents</Tabs.Tab>
          {!isArrival && (
            <Tabs.Tab value="batches" data-pw="house-consigment-batches-tab">
              Batches
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="house-consignment">
          <HouseConsignmentData
            form={form}
            houseForm={houseForm}
            disabled={editDisabled}
            editDisabledConditionalForArrival={editDisabledConditionalForArrival}
          />
        </Tabs.Panel>
        <Tabs.Panel value="stakeholders">
          <HouseStakeholders form={form} houseForm={houseForm} disabled={editDisabled} />
        </Tabs.Panel>
        <Tabs.Panel value="transport">
          <HouseTransport form={form} houseForm={houseForm} disabled={editDisabled} editDisabledConditionalForArrival={editDisabledConditionalForArrival} />
        </Tabs.Panel>
        <Tabs.Panel value="documents">
          <HouseDocuments
            form={form}
            houseForm={houseForm}
            disabled={editDisabled}
            editDisabledConditionalForArrival={editDisabledConditionalForArrival}
            isArrival={isArrival}
          />
        </Tabs.Panel>
        <Tabs.Panel value="batches">
          <HouseBatches houseForm={houseForm} disabled={editDisabled} />
        </Tabs.Panel>
      </Tabs>
      <Box className={classes.footer}>
        <Button type="button" onClick={onHide} size="lg" variant="transparent">
          Cancel
        </Button>
        {(!editDisabled || !editDisabledConditionalForArrival) && (
          <Button type="button" variant="filled" size="lg" onClick={handleSave} data-pw="save-house-consignment-btn">
            Save
          </Button>
        )}
      </Box>
    </Stack>
  );
};
